import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input, Textarea } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { FormRow } from '../../components/ui/FormRow';
import { Spinner } from '../../components/ui/Spinner';
import { fmt } from '../../lib/formatters';
import { useVolver } from '../../lib/useVolver';
import { supabase } from '../../lib/supabase';
import { toast, confirmar } from '../../lib/toast';

// ─── Tipos ────────────────────────────────────────────────────────────

type Candidato = {
  id: string;
  valoracion: number | null;
  pros: string | null;
  contras: string | null;
  seleccionado: boolean;
  ordering: number;
  producto_proveedor_id: string;
  ref_proveedor: string;
  pp_nombre: string;
  pp_descripcion: string | null;
  pp_main_specs: string | null;
  coste_usd: number | null;
  moq: number | null;
  lead_time_dias: number | null;
  pp_foc_pct: number | null;
  proveedor_foc_pct: number | null;
  sample: string | null;
  pp_created_at: string;
  subcategoria_nombre: string | null;
  detalle_nombre: string | null;
  opciones_count: number | null;
  proveedor_nombre: string | null;
  proveedor_contacto: string | null;
};

type Importancia = 'URGENTE' | 'ALTA' | 'MEDIA' | 'BAJA';

type NuevoProductoFull = {
  id: string;
  nombre: string;
  descripcion: string | null;
  marca_id: string;
  marca_nombre: string | null;
  cliente_codigo: string | null;
  cliente_nombre: string | null;
  gama_id: string | null;
  gama_nombre: string | null;
  categoria_id: string | null;
  categoria_nombre: string | null;
  subcategoria_id: string | null;
  subcategoria_nombre: string | null;
  detalle_id: string | null;
  detalle_nombre: string | null;
  segmento: string | null;
  modelo_interno: string | null;
  main_specs: string | null;
  compatibilidad: string | null;
  examples: string[] | null;
  pvpr_iva_eur: number | null;
  margin_target_pct: number | null;
  coste_target_usd: number | null;
  importancia: Importancia;
  estado: string;
  producto_final_id: string | null;
  producto_final_sku: string | null;
  producto_final_nombre: string | null;
  notas: string | null;
  origen: string | null;
  created_at: string;
  updated_at: string;
  candidatos: Candidato[];
};

type Archivo = {
  id: string;
  tipo: 'imagen' | 'adjunto';
  storage_path: string;
  nombre_original: string;
  mime_type: string;
  bytes: number;
  ordering: number;
  notas: string | null;
  url: string | null;
};

type SugerenciaCandidato = {
  id: string;
  ref_proveedor: string;
  nombre: string;
  descripcion: string | null;
  coste_usd: number | string | null;
  moq: number | null;
  lead_time_dias: number | null;
  gama_nombre: string | null;
  categoria_nombre: string | null;
  subcategoria_nombre: string | null;
  proveedor_id: string | null;
  proveedor_nombre: string | null;
  bucket: number;
  distancia_target_usd: number | string | null;
};

// ─── Diccionarios de display ──────────────────────────────────────────

const ESTADO_LABEL: Record<string, string> = {
  BUSCANDO: 'Buscando candidatos',
  CON_CANDIDATOS: 'Candidatos asociados',
  DECIDIDO: 'Ganador elegido',
  CONVERTIDO: 'Convertido a producto',
  CANCELADO: 'Cancelado',
};
const ESTADO_COLOR: Record<string, string> = {
  BUSCANDO: '#94a3b8',
  CON_CANDIDATOS: '#3b82f6',
  DECIDIDO: '#8b5cf6',
  CONVERTIDO: '#10b981',
  CANCELADO: '#ef4444',
};

const IMPORTANCIA_OPTIONS: { value: Importancia; label: string; color: string }[] = [
  { value: 'URGENTE', label: 'Urgente', color: '#dc2626' },
  { value: 'ALTA',    label: 'Alta',    color: '#ea580c' },
  { value: 'MEDIA',   label: 'Media',   color: '#0891b2' },
  { value: 'BAJA',    label: 'Baja',    color: '#64748b' },
];
const IMPORTANCIA_COLOR: Record<Importancia, string> = Object.fromEntries(
  IMPORTANCIA_OPTIONS.map((i) => [i.value, i.color] as const),
) as Record<Importancia, string>;
const IMPORTANCIA_LABEL: Record<Importancia, string> = Object.fromEntries(
  IMPORTANCIA_OPTIONS.map((i) => [i.value, i.label] as const),
) as Record<Importancia, string>;

const BUCKET_LABEL: Record<number, string> = {
  1: '🎯 Mismo DETALLE que el proyecto',
  2: '✓ Coste hasta −20% del target (barato o igual)',
  3: '~ Coste hasta +20% del target (un poco caro)',
  4: '· Resto del mismo subtipo (por alejamiento al target)',
};
const BUCKET_BADGE_COLOR: Record<number, string> = {
  1: '#059669',
  2: '#10b981',
  3: '#f59e0b',
  4: '#94a3b8',
};

// ─── Helpers ──────────────────────────────────────────────────────────

const isUrl = (s: string): boolean => /^https?:\/\//i.test(s.trim());

// ─── Borrador para modo edit ──────────────────────────────────────────

type Draft = {
  nombre: string;
  gama_id: string;
  categoria_id: string;
  subcategoria_id: string;
  detalle_id: string;
  segmento: string;
  modelo_interno: string;
  main_specs: string;
  compatibilidad: string;
  notas: string;
  importancia: Importancia;
  pvpr: string;
  margin: string;
  coste_target: string;
  origen: string;
};

function draftFromProyecto(np: NuevoProductoFull): Draft {
  return {
    nombre: np.nombre ?? '',
    gama_id: np.gama_id ?? '',
    categoria_id: np.categoria_id ?? '',
    subcategoria_id: np.subcategoria_id ?? '',
    detalle_id: np.detalle_id ?? '',
    segmento: np.segmento ?? '',
    modelo_interno: np.modelo_interno ?? '',
    main_specs: np.main_specs ?? '',
    compatibilidad: np.compatibilidad ?? '',
    notas: np.notas ?? '',
    importancia: np.importancia,
    pvpr: np.pvpr_iva_eur != null ? String(np.pvpr_iva_eur) : '',
    margin: np.margin_target_pct != null ? String(np.margin_target_pct) : '',
    coste_target: np.coste_target_usd != null ? String(np.coste_target_usd) : '',
    origen: np.origen ?? '',
  };
}

function patchFromDraft(d: Draft): Record<string, unknown> {
  return {
    nombre: d.nombre.trim(),
    gama_id: d.gama_id || null,
    categoria_id: d.categoria_id || null,
    subcategoria_id: d.subcategoria_id || null,
    detalle_id: d.detalle_id || null,
    segmento: d.segmento.trim() || null,
    modelo_interno: d.modelo_interno.trim() || null,
    main_specs: d.main_specs.trim() || null,
    compatibilidad: d.compatibilidad.trim() || null,
    notas: d.notas.trim() || null,
    importancia: d.importancia,
    pvpr_iva_eur: d.pvpr === '' ? null : Number(d.pvpr),
    margin_target_pct: d.margin === '' ? null : Number(d.margin),
    coste_target_usd: d.coste_target === '' ? null : Number(d.coste_target),
    origen: d.origen.trim() || null,
  };
}

// ─── Componente principal ────────────────────────────────────────────

export function NuevoProductoDetalle() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const volver = useVolver('/nuevos-productos');
  const qc = useQueryClient();

  const data = useQuery({
    queryKey: ['nuevo-producto', id, 'full'],
    queryFn: () => api.get<NuevoProductoFull>(`/nuevos-productos/${id}/full`),
    enabled: !!id,
  });

  const gamas = useQuery({
    queryKey: ['list', '/admin/gamas'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/gamas'),
  });
  const categorias = useQuery({
    queryKey: ['list', '/admin/categorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; gama_id: string }[] }>('/admin/categorias'),
  });
  const subcategorias = useQuery({
    queryKey: ['list', '/admin/subcategorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; categoria_id: string }[] }>('/admin/subcategorias'),
  });
  const detalles = useQuery({
    queryKey: ['list', '/admin/detalles'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; subcategoria_id: string; activo: boolean }[] }>('/admin/detalles'),
  });
  const archivos = useQuery({
    queryKey: ['nuevo-producto', id, 'archivos'],
    queryFn: () => api.get<{ data: Archivo[]; storage_configured: boolean }>(`/nuevos-productos/${id}/archivos`),
    enabled: !!id,
  });

  // ─── Mutations comunes ────────────────────────────────────────────
  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.put(`/nuevos-productos/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'full'] }),
  });
  const addCandidato = useMutation({
    mutationFn: (body: { producto_proveedor_id: string }) => api.post(`/nuevos-productos/${id}/candidatos`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'full'] }),
  });
  const seleccionar = useMutation({
    mutationFn: (candId: string) => api.post(`/nuevos-productos/${id}/candidatos/${candId}/seleccionar`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'full'] }),
  });
  const valorar = useMutation({
    mutationFn: ({ candId, body }: { candId: string; body: Record<string, unknown> }) => api.put(`/nuevos-productos/${id}/candidatos/${candId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'full'] }),
  });
  const eliminarCand = useMutation({
    mutationFn: (candId: string) => api.delete(`/nuevos-productos/${id}/candidatos/${candId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'full'] }),
  });
  const reordenar = useMutation({
    mutationFn: (ids: string[]) => api.patch(`/nuevos-productos/${id}/candidatos/reordenar`, { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'full'] }),
  });
  const convertir = useMutation({
    mutationFn: (body: { sku: string; ean?: string; coste?: number | null; pvpr?: number | null }) =>
      api.post<{ id: string }>(`/nuevos-productos/${id}/convertir`, body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'full'] });
      navigate(`/productos/${res.id}`);
    },
  });
  const eliminar = useMutation({
    mutationFn: () => api.delete(`/nuevos-productos/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nuevos-productos'] });
      navigate('/nuevos-productos');
    },
  });
  const recalcular = useMutation({
    mutationFn: () => api.post<{ coste_target_usd: number; inviable: boolean }>(`/nuevos-productos/${id}/recalcular-coste-target`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'full'] }),
  });
  // Recalcular sobre el draft sin persistir (modo edit): usa los valores
  // que el CEO está editando, no los guardados. El resultado se mete en
  // draft.coste_target y queda pendiente del Guardar.
  const recalcularDraft = useMutation({
    mutationFn: (body: { pvpr_iva_eur: number; margin_target_pct: number; marca_id: string; gama_id: string | null; categoria_id: string | null; subcategoria_id: string | null }) =>
      api.post<{ coste_target_usd: number; inviable: boolean }>('/precio/calcular-coste-target-auto', body),
  });

  // ─── Modo edit ────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);

  // ─── Modals ───────────────────────────────────────────────────────
  const [addOpen, setAddOpen] = useState(false);
  const [searchPP, setSearchPP] = useState('');
  const [previewPP, setPreviewPP] = useState<string | null>(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({ sku: '', ean: '', coste: '', pvpr: '' });
  const [newExample, setNewExample] = useState('');
  const [editCandId, setEditCandId] = useState<string | null>(null);
  const [candDraft, setCandDraft] = useState<{ valoracion: string; pros: string; contras: string } | null>(null);

  // ─── Sugerencias de candidatos (cuando se abre el modal) ──────────
  const sugerencias = useQuery({
    queryKey: ['nuevo-producto', id, 'sugerencias-candidatos', searchPP],
    queryFn: () => api.get<{ data: SugerenciaCandidato[]; contexto: { coste_target_usd: number | null } }>(
      `/nuevos-productos/${id}/sugerencias-candidatos${searchPP ? `?q=${encodeURIComponent(searchPP)}` : ''}`,
    ),
    enabled: !!id && addOpen,
  });

  // ─── Upload de imágenes ───────────────────────────────────────────
  const fileInput = useRef<HTMLInputElement | null>(null);
  const uploadImage = useMutation({
    mutationFn: async (file: File) => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', 'imagen');
      const res = await fetch(`/api/nuevos-productos/${id}/archivos`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Upload falló (${res.status}): ${txt}`);
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'archivos'] }),
  });
  const deleteArchivo = useMutation({
    mutationFn: (archivoId: string) => api.delete(`/nuevos-productos/${id}/archivos/${archivoId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'archivos'] }),
  });

  const np = data.data;

  const categoriasForGama = useMemo(() => {
    const all = categorias.data?.data ?? [];
    const gamaId = editing ? draft?.gama_id : np?.gama_id;
    if (!gamaId) return all;
    return all.filter((c) => c.gama_id === gamaId);
  }, [editing, draft?.gama_id, np?.gama_id, categorias.data]);
  const subcatsForCategoria = useMemo(() => {
    const all = subcategorias.data?.data ?? [];
    const catId = editing ? draft?.categoria_id : np?.categoria_id;
    if (!catId) return all;
    return all.filter((s) => s.categoria_id === catId);
  }, [editing, draft?.categoria_id, np?.categoria_id, subcategorias.data]);
  const detallesForSub = useMemo(() => {
    const all = (detalles.data?.data ?? []).filter((d) => d.activo);
    const subId = editing ? draft?.subcategoria_id : np?.subcategoria_id;
    if (!subId) return [];
    return all.filter((d) => d.subcategoria_id === subId);
  }, [editing, draft?.subcategoria_id, np?.subcategoria_id, detalles.data]);

  if (data.isLoading) return <Spinner />;
  if (data.isError || !data.data || !np) {
    return (
      <div>
        <PageHeader title="Proyecto no encontrado" />
        <Button variant="secondary" onClick={volver}>← Volver</Button>
      </div>
    );
  }

  const ganador = np.candidatos.find((c) => c.seleccionado);
  const costeTargetUsd = np.coste_target_usd ?? null;
  const examplesDraft = Array.isArray(np.examples) ? np.examples : [];

  const startEdit = () => {
    setDraft(draftFromProyecto(np));
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };
  const saveEdit = async () => {
    if (!draft) return;
    if (!draft.nombre.trim()) {
      toast.info('El nombre del proyecto no puede quedar vacío');
      return;
    }
    const patch = patchFromDraft(draft);
    await update.mutateAsync(patch);
    setEditing(false);
    setDraft(null);
    recalcularDraft.reset();
  };

  // Botón "Recalcular AUTO" cuando estamos en modo edit: no persiste,
  // solo coloca el valor calculado en draft.coste_target para que el CEO
  // lo vea, lo pueda redondear y luego pulse "Guardar".
  const recalcularEnDraft = async () => {
    if (!draft || !np.marca_id) return;
    const pvpr = draft.pvpr === '' ? 0 : Number(draft.pvpr);
    const margin = draft.margin === '' ? 0 : Number(draft.margin);
    if (pvpr <= 0 || margin <= 0) return;
    const out = await recalcularDraft.mutateAsync({
      pvpr_iva_eur: pvpr,
      margin_target_pct: margin,
      marca_id: np.marca_id,
      gama_id: draft.gama_id || null,
      categoria_id: draft.categoria_id || null,
      subcategoria_id: draft.subcategoria_id || null,
    });
    setDraft((d) => (d ? { ...d, coste_target: String(out.coste_target_usd) } : d));
  };

  const loadMarginDefault = async () => {
    if (!draft || !np.marca_id) return;
    try {
      const params = new URLSearchParams({ marca_id: np.marca_id });
      if (draft.gama_id) params.set('gama_id', draft.gama_id);
      if (draft.categoria_id) params.set('categoria_id', draft.categoria_id);
      if (draft.subcategoria_id) params.set('subcategoria_id', draft.subcategoria_id);
      const r = await api.get<{ margin_target_pct: number | null }>(`/nuevos-productos/margin-default?${params.toString()}`);
      if (r.margin_target_pct != null) {
        setDraft((d) => (d ? { ...d, margin: String(r.margin_target_pct) } : d));
      }
    } catch {
      // ignore
    }
  };

  // Helpers de UI para el modo lectura
  const readField = (v: string | null | undefined) => (
    v && v.length ? <span className="whitespace-pre-line text-sm text-slate-800">{v}</span> : <span className="text-sm text-slate-400">—</span>
  );

  return (
    <div>
      <PageHeader
        title={np.modelo_interno || np.nombre || 'Proyecto sin nombre'}
        subtitle={
          <>
            <span>{np.marca_nombre ?? '—'} · {np.cliente_nombre ?? '—'} · {np.gama_nombre ?? '—'} / {np.categoria_nombre ?? '—'} / {np.subcategoria_nombre ?? '—'}</span>
            <span className="ml-3 text-xs text-slate-400">
              Creado {fmt.date(np.created_at)} · Editado {fmt.datetime(np.updated_at)}
            </span>
          </>
        }
        actions={
          <>
            <Badge color={IMPORTANCIA_COLOR[np.importancia]}>{IMPORTANCIA_LABEL[np.importancia]}</Badge>
            <Badge color={ESTADO_COLOR[np.estado] ?? '#94a3b8'}>{ESTADO_LABEL[np.estado] ?? np.estado}</Badge>
            <Button variant="secondary" onClick={volver}>← Volver</Button>
            {!editing && (
              <Button variant="edit" onClick={startEdit}>✏️ Editar</Button>
            )}
            {editing && (
              <>
                <Button variant="danger" onClick={cancelEdit} disabled={update.isPending}>Cancelar</Button>
                <Button variant="save" onClick={saveEdit} disabled={update.isPending}>
                  {update.isPending ? 'Guardando…' : 'Guardar'}
                </Button>
              </>
            )}
            {ganador && np.estado === 'DECIDIDO' && !editing && (
              <Button onClick={() => setConvertOpen(true)}>Convertir a producto</Button>
            )}
            {np.producto_final_id && !editing && (
              <Button variant="secondary" onClick={() => navigate(`/productos/${np.producto_final_id}`)}>
                Ver producto · {np.producto_final_sku}
              </Button>
            )}
            {!editing && (
              <Button
                variant="ghost"
                className="text-rose-600"
                onClick={async () => {
                  if (await confirmar({ title: `¿Eliminar el proyecto "${np.modelo_interno || np.nombre}"?`, confirmLabel: 'Eliminar', danger: true })) eliminar.mutate();
                }}
                disabled={eliminar.isPending}
              >
                {eliminar.isPending ? 'Eliminando…' : 'Eliminar'}
              </Button>
            )}
          </>
        }
      />

      {/* ─── 4 cuadros: MAIN SPECS / PVPR / MARGIN / COSTE TARGET ─── */}
      <div className="mb-6 grid gap-4 md:grid-cols-4">
        <Card title="Main specs" description="Resumen técnico destacado.">
          {editing ? (
            <Textarea
              rows={6}
              value={draft?.main_specs ?? ''}
              onChange={(e) => setDraft((d) => (d ? { ...d, main_specs: e.target.value } : d))}
            />
          ) : np.main_specs ? (
            <p className="whitespace-pre-line text-sm text-slate-800">{np.main_specs}</p>
          ) : (
            <p className="text-sm italic text-slate-400">Sin main specs.</p>
          )}
        </Card>
        <Card title="PVPR (con IVA)">
          {editing ? (
            <Input
              type="number"
              step="any"
              value={draft?.pvpr ?? ''}
              onChange={(e) => setDraft((d) => (d ? { ...d, pvpr: e.target.value } : d))}
              placeholder="€"
            />
          ) : (
            <p className="text-2xl font-semibold text-slate-900">
              {np.pvpr_iva_eur != null ? fmt.eur(np.pvpr_iva_eur) : '—'}
            </p>
          )}
          <p className="mt-1 text-xs text-slate-500">PVPR objetivo con IVA en el cliente.</p>
        </Card>
        <Card title="Margin target PVPR cliente">
          {editing ? (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="any"
                value={draft?.margin ?? ''}
                onChange={(e) => setDraft((d) => (d ? { ...d, margin: e.target.value } : d))}
                placeholder="%"
              />
              <Button size="sm" variant="ghost" onClick={loadMarginDefault} title="Cargar margen target por defecto desde Parámetros">
                Cargar default
              </Button>
            </div>
          ) : (
            <p className="text-2xl font-semibold text-slate-900">
              {np.margin_target_pct != null ? fmt.pct(np.margin_target_pct) : '—'}
            </p>
          )}
          <p className="mt-1 text-xs text-slate-500">Margen objetivo entre PVPR sin IVA y coste final.</p>
        </Card>
        <Card
          title="Coste target (FOB)"
          actions={editing ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={recalcularDraft.isPending || !draft || !np.marca_id || !draft.pvpr || !draft.margin}
              onClick={recalcularEnDraft}
              title="Calcula contra los parámetros actuales y pone el resultado en el campo (puedes redondearlo manualmente antes de guardar)"
            >
              {recalcularDraft.isPending ? 'Calculando…' : '↻ Recalcular AUTO'}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              disabled={recalcular.isPending || np.pvpr_iva_eur == null || np.margin_target_pct == null}
              onClick={() => recalcular.mutate()}
              title="Recalcular contra los parámetros actuales y guardar"
            >
              {recalcular.isPending ? 'Recalculando…' : '↻ Recalcular AUTO'}
            </Button>
          )}
        >
          {editing ? (
            <>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="any"
                  value={draft?.coste_target ?? ''}
                  onChange={(e) => setDraft((d) => (d ? { ...d, coste_target: e.target.value } : d))}
                  placeholder="USD FOB"
                />
                <span className="text-xs text-slate-500">USD</span>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Edita manualmente o pulsa &quot;↻ Recalcular AUTO&quot; para calcularlo a partir de PVPR y Margin. Puedes redondearlo a mano antes de guardar.
              </p>
              {recalcularDraft.data?.inviable && (
                <p className="mt-1 text-xs text-rose-600">PVPR + margen no son alcanzables con las condiciones actuales.</p>
              )}
              {recalcularDraft.error && (
                <p className="mt-1 text-xs text-rose-600">{(recalcularDraft.error as Error).message}</p>
              )}
            </>
          ) : (
            <>
              <p className="text-3xl font-bold text-emerald-700">
                {costeTargetUsd != null ? fmt.usd(costeTargetUsd) : '—'}
              </p>
              {recalcular.data?.inviable && (
                <p className="mt-1 text-xs text-rose-600">PVPR + margen no son alcanzables con las condiciones actuales.</p>
              )}
              {!recalcular.data?.inviable && costeTargetUsd != null && (
                <p className="mt-1 text-xs text-slate-500">Coste máximo en USD FOB que podemos pagar al proveedor.</p>
              )}
              {recalcular.error && (
                <p className="mt-1 text-xs text-rose-600">{(recalcular.error as Error).message}</p>
              )}
            </>
          )}
        </Card>
      </div>

      {/* ─── 3 columnas: Datos · Examples · Imágenes ─────────────── */}
      {!editing && (
        <div className="mb-6 grid gap-4 md:grid-cols-12">
          <DatosCol np={np} className="md:col-span-6" />
          <ExamplesCol
            examples={examplesDraft}
            onAdd={(s) => update.mutate({ examples: [...examplesDraft, s] })}
            onRemove={(i) => update.mutate({ examples: examplesDraft.filter((_, idx) => idx !== i) })}
            value={newExample}
            onChangeValue={setNewExample}
            className="md:col-span-3"
          />
          <ImagenesCol
            archivos={archivos.data?.data ?? []}
            onUpload={(file) => uploadImage.mutate(file)}
            uploading={uploadImage.isPending}
            onDelete={(arId) => deleteArchivo.mutate(arId)}
            className="md:col-span-3"
          />
        </div>
      )}

      {/* ─── Datos del brief (modo edit, formato vertical) ──────── */}
      {editing && (
      <Card title="Brief del proyecto" description="Modifica los campos y pulsa Guardar arriba.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="sm:col-span-2 lg:col-span-3">
            <FormRow label="Nombre" hint="Nombre del proyecto / brief de búsqueda.">
              {editing ? (
                <Input
                  value={draft?.nombre ?? ''}
                  onChange={(e) => setDraft((d) => (d ? { ...d, nombre: e.target.value } : d))}
                  placeholder="Nombre del proyecto"
                />
              ) : (
                readField(np.nombre)
              )}
            </FormRow>
          </div>
          <FormRow label="Importancia">
            {editing ? (
              <Select
                options={IMPORTANCIA_OPTIONS.map((i) => ({ value: i.value, label: i.label }))}
                value={draft?.importancia ?? 'MEDIA'}
                onChange={(e) => setDraft((d) => (d ? { ...d, importancia: e.target.value as Importancia } : d))}
              />
            ) : (
              <Badge color={IMPORTANCIA_COLOR[np.importancia]}>{IMPORTANCIA_LABEL[np.importancia]}</Badge>
            )}
          </FormRow>
          <FormRow label="Gama">
            {editing ? (
              <Select
                options={[{ value: '', label: '—' }, ...(gamas.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))]}
                value={draft?.gama_id ?? ''}
                emptyLabel={null}
                onChange={(e) => setDraft((d) => (d ? { ...d, gama_id: e.target.value, categoria_id: '', subcategoria_id: '' } : d))}
              />
            ) : (
              readField(np.gama_nombre)
            )}
          </FormRow>
          <FormRow label="Categoría">
            {editing ? (
              <Select
                options={[{ value: '', label: '—' }, ...categoriasForGama.map((c) => ({ value: c.id, label: c.nombre }))]}
                value={draft?.categoria_id ?? ''}
                emptyLabel={null}
                onChange={(e) => setDraft((d) => (d ? { ...d, categoria_id: e.target.value, subcategoria_id: '' } : d))}
              />
            ) : (
              readField(np.categoria_nombre)
            )}
          </FormRow>
          <FormRow label="Subcategoría">
            {editing ? (
              <Select
                options={[{ value: '', label: '—' }, ...subcatsForCategoria.map((s) => ({ value: s.id, label: s.nombre }))]}
                value={draft?.subcategoria_id ?? ''}
                emptyLabel={null}
                onChange={(e) => setDraft((d) => (d ? { ...d, subcategoria_id: e.target.value, detalle_id: '' } : d))}
              />
            ) : (
              readField(np.subcategoria_nombre)
            )}
          </FormRow>
          <FormRow label="Detalle">
            {editing ? (
              <Select
                options={[{ value: '', label: '—' }, ...detallesForSub.map((d) => ({ value: d.id, label: d.nombre }))]}
                value={draft?.detalle_id ?? ''}
                emptyLabel={null}
                onChange={(e) => setDraft((d) => (d ? { ...d, detalle_id: e.target.value } : d))}
                disabled={!draft?.subcategoria_id}
              />
            ) : (
              readField(np.detalle_nombre)
            )}
          </FormRow>
          <FormRow label="Segmento">
            {editing ? (
              <Input
                value={draft?.segmento ?? ''}
                onChange={(e) => setDraft((d) => (d ? { ...d, segmento: e.target.value } : d))}
              />
            ) : (
              readField(np.segmento)
            )}
          </FormRow>
          <FormRow label="Modelo interno">
            {editing ? (
              <Input
                value={draft?.modelo_interno ?? ''}
                onChange={(e) => setDraft((d) => (d ? { ...d, modelo_interno: e.target.value } : d))}
              />
            ) : (
              readField(np.modelo_interno)
            )}
          </FormRow>
          <div className="sm:col-span-2 lg:col-span-3">
            <FormRow label="Compatibilidad" hint="Plataformas/dispositivos compatibles. Texto libre.">
              {editing ? (
                <Textarea
                  rows={2}
                  value={draft?.compatibilidad ?? ''}
                  onChange={(e) => setDraft((d) => (d ? { ...d, compatibilidad: e.target.value } : d))}
                  placeholder="Ej: PS5, Xbox Series X · iPhone 15+ · USB-C únicamente…"
                />
              ) : (
                readField(np.compatibilidad)
              )}
            </FormRow>
          </div>
          <FormRow label="Origen">
            {editing ? (
              <Input
                value={draft?.origen ?? ''}
                onChange={(e) => setDraft((d) => (d ? { ...d, origen: e.target.value } : d))}
                placeholder="IMPORTACION, MANUAL, IDEA…"
              />
            ) : (
              readField(np.origen)
            )}
          </FormRow>
          <FormRow label="Fecha creación">
            <span className="text-sm text-slate-700">{fmt.date(np.created_at)}</span>
          </FormRow>
          <div className="sm:col-span-2 lg:col-span-3">
            <FormRow label="Main specs">
              {editing ? (
                <Textarea
                  rows={3}
                  value={draft?.main_specs ?? ''}
                  onChange={(e) => setDraft((d) => (d ? { ...d, main_specs: e.target.value } : d))}
                />
              ) : (
                readField(np.main_specs)
              )}
            </FormRow>
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <FormRow label="Notas">
              {editing ? (
                <Textarea
                  rows={2}
                  value={draft?.notas ?? ''}
                  onChange={(e) => setDraft((d) => (d ? { ...d, notas: e.target.value } : d))}
                />
              ) : (
                readField(np.notas)
              )}
            </FormRow>
          </div>
        </div>
      </Card>
      )}

      <div className="h-4" />

      {/* ─── Examples (modo edit only — read mode usa la grid de 3 cols) ──── */}
      {editing && (
      <Card title="Examples (URLs o palabras de referencia)" description="Lista de referencias. Las acciones se ejecutan al instante.">
        <div className="flex gap-2">
          <Input
            value={newExample}
            placeholder="https://... o texto descriptivo"
            onChange={(e) => setNewExample(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newExample.trim()) {
                const next = [...examplesDraft, newExample.trim()];
                update.mutate({ examples: next });
                setNewExample('');
              }
            }}
          />
          <Button
            variant="create"
            disabled={!newExample.trim()}
            onClick={() => {
              const next = [...examplesDraft, newExample.trim()];
              update.mutate({ examples: next });
              setNewExample('');
            }}
          >
            +
          </Button>
        </div>
        {examplesDraft.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">Sin ejemplos aún.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {examplesDraft.map((ex, idx) => (
              <li key={`${idx}-${ex}`} className="flex items-center justify-between py-2">
                {isUrl(ex) ? (
                  <a href={ex} target="_blank" rel="noopener noreferrer" className="truncate text-sm text-blue-600 hover:underline">{ex}</a>
                ) : (
                  <span className="truncate text-sm text-slate-700">{ex}</span>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-rose-600"
                  onClick={() => {
                    const next = examplesDraft.filter((_, i) => i !== idx);
                    update.mutate({ examples: next });
                  }}
                >
                  Quitar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      )}

      {/* ─── Galería de imágenes (modo edit only) ─────────────────── */}
      {editing && (<>
      <div className="h-4" />
      <Card
        title="Imágenes de referencia"
        actions={
          <>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadImage.mutate(file);
                if (fileInput.current) fileInput.current.value = '';
              }}
            />
            <Button variant="create" size="sm" disabled={uploadImage.isPending} onClick={() => fileInput.current?.click()}>
              {uploadImage.isPending ? 'Subiendo…' : '+ Subir imagen'}
            </Button>
          </>
        }
      >
        {(archivos.data?.data ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">Sin imágenes aún.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {(archivos.data?.data ?? []).filter((a) => a.tipo === 'imagen').map((a) => (
              <div key={a.id} className="group relative overflow-hidden rounded border border-slate-200">
                {a.url ? (
                  <img src={a.url} alt={a.nombre_original} className="aspect-square w-full object-cover" />
                ) : (
                  <div className="aspect-square w-full bg-slate-100 text-xs text-slate-400">No URL</div>
                )}
                <button
                  className="absolute right-1 top-1 hidden rounded bg-rose-600 px-2 py-1 text-xs text-white group-hover:block"
                  onClick={async () => { if (await confirmar({ title: '¿Eliminar imagen?', confirmLabel: 'Eliminar', danger: true })) deleteArchivo.mutate(a.id); }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
      </>)}

      <div className="h-4" />

      {/* ─── Candidatos asociados — formato lista ────────────────── */}
      <Card
        title="Candidatos asociados"
        description="Click en el nombre para vista rápida. ↑↓ reordena. Estrella = ganador."
        actions={<Button variant="create" size="sm" onClick={() => setAddOpen(true)}>+ Asociar candidato</Button>}
      >
        {np.candidatos.length === 0 ? (
          <p className="text-sm text-slate-500">Aún no hay candidatos asociados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-2 text-left">#</th>
                  <th className="px-2 py-2 text-left">Fecha</th>
                  <th className="px-2 py-2 text-left">Proveedor</th>
                  <th className="px-2 py-2 text-left">Ref.</th>
                  <th className="px-2 py-2 text-left">Nombre</th>
                  <th className="px-2 py-2 text-left">Subtipo</th>
                  <th className="px-2 py-2 text-left">Detalle</th>
                  <th className="px-2 py-2 text-left">Sample</th>
                  <th className="px-2 py-2 text-right">Precio</th>
                  <th className="px-2 py-2 text-right">MOQ</th>
                  <th className="px-2 py-2 text-right">F.O.C.</th>
                  <th className="px-2 py-2 text-center">Opc.</th>
                  <th className="px-2 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {np.candidatos.map((c, idx) => {
                  const diffPct = costeTargetUsd != null && c.coste_usd != null && costeTargetUsd > 0
                    ? ((Number(c.coste_usd) - Number(costeTargetUsd)) / Number(costeTargetUsd)) * 100
                    : null;
                  const ok = diffPct != null && diffPct <= 0;
                  const sampleBadge = c.sample
                    ? { SI: { c: '#10b981', l: 'Sí' }, SOLICITADO: { c: '#f59e0b', l: 'Solicitado' }, NO: { c: '#94a3b8', l: 'No' } }[c.sample]
                    : null;
                  const focEfectivo = c.pp_foc_pct ?? c.proveedor_foc_pct ?? null;
                  return (
                    <tr key={c.id} className={c.seleccionado ? 'bg-emerald-50' : ''}>
                      <td className="whitespace-nowrap px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-xs font-semibold text-slate-500">#{idx + 1}</span>
                          {c.seleccionado && <Badge color="#10b981">★</Badge>}
                        </div>
                        <div className="mt-0.5 flex justify-center gap-0.5">
                          <button
                            type="button"
                            disabled={idx === 0}
                            onClick={() => {
                              const ids = np.candidatos.map((x) => x.id);
                              [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
                              reordenar.mutate(ids);
                            }}
                            className="cursor-pointer text-xs text-slate-400 disabled:opacity-30 hover:text-slate-800"
                          >↑</button>
                          <button
                            type="button"
                            disabled={idx === np.candidatos.length - 1}
                            onClick={() => {
                              const ids = np.candidatos.map((x) => x.id);
                              [ids[idx + 1], ids[idx]] = [ids[idx], ids[idx + 1]];
                              reordenar.mutate(ids);
                            }}
                            className="cursor-pointer text-xs text-slate-400 disabled:opacity-30 hover:text-slate-800"
                          >↓</button>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs text-slate-500">{fmt.date(c.pp_created_at)}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs">{c.proveedor_nombre ?? '—'}</td>
                      <td className="whitespace-nowrap px-2 py-2 font-mono text-xs">{c.ref_proveedor}</td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => setPreviewPP(c.producto_proveedor_id)}
                          className="cursor-pointer text-left text-sm text-slate-900 hover:underline"
                          title="Ver ficha reducida"
                        >
                          {c.pp_nombre}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs">{c.subcategoria_nombre ?? '—'}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs">{c.detalle_nombre ?? '—'}</td>
                      <td className="px-2 py-2">
                        {sampleBadge ? <Badge color={sampleBadge.c}>{sampleBadge.l}</Badge> : <span className="text-xs text-slate-300">—</span>}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right">
                        <div className="font-mono text-sm">{fmt.usd(c.coste_usd)}</div>
                        {diffPct != null && (
                          <div className={`text-[10px] font-semibold ${ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {ok ? '−' : '+'}{Math.abs(diffPct).toFixed(1)}%
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right text-xs">{fmt.number(c.moq)}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-right text-xs">{focEfectivo != null ? fmt.pct(focEfectivo) : '—'}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-center text-xs">
                        {c.opciones_count != null && c.opciones_count > 0
                          ? <Badge color="#3b82f6">{c.opciones_count}</Badge>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          {!c.seleccionado && (
                            <button
                              type="button"
                              onClick={() => seleccionar.mutate(c.id)}
                              className="cursor-pointer rounded px-1.5 py-0.5 text-xs text-emerald-700 hover:bg-emerald-50"
                              title="Marcar como ganador"
                            >★</button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setCandDraft({
                                valoracion: c.valoracion != null ? String(c.valoracion) : '',
                                pros: c.pros ?? '',
                                contras: c.contras ?? '',
                              });
                              setEditCandId(c.id);
                            }}
                            className="cursor-pointer rounded px-1.5 py-0.5 text-xs text-amber-700 hover:bg-amber-50"
                            title={`Valorar (${c.valoracion ?? '—'}/5)`}
                          >✏️</button>
                          <button
                            type="button"
                            onClick={async () => { if (await confirmar({ title: '¿Quitar este candidato?', confirmLabel: 'Quitar', danger: true })) eliminarCand.mutate(c.id); }}
                            className="cursor-pointer rounded px-1.5 py-0.5 text-xs text-rose-700 hover:bg-rose-50"
                            title="Quitar"
                          >×</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="h-4" />

      {/* ─── Tabla comparativa de candidatos ─────────────────────── */}
      {np.candidatos.length > 1 && (
        <Card
          title="Tabla comparativa"
          description="Productos en columnas, atributos en filas. Útil para decidir entre candidatos."
        >
          <ComparativaTable
            candidatos={np.candidatos}
            target={costeTargetUsd}
            onClickPP={(ppId) => setPreviewPP(ppId)}
          />
        </Card>
      )}

      {/* ─── Modal: asociar candidato con buscador inteligente ──── */}
      <Modal
        open={addOpen}
        onClose={() => { setAddOpen(false); setSearchPP(''); }}
        title="Asociar candidato"
        size="xl"
      >
        <div className="space-y-3">
          <div>
            <Input
              autoFocus
              placeholder="Buscar por nombre, ref. proveedor o proveedor…"
              value={searchPP}
              onChange={(e) => setSearchPP(e.target.value)}
            />
            <p className="mt-0.5 text-[10px] leading-tight text-slate-400">
              Varias palabras = todas deben coincidir · <span className="font-medium">palabra1 / palabra2</span> = cualquiera coincide · <span className="font-medium">"frase exacta"</span> = coincidencia literal
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Ordenado por afinidad: mismo subtipo → proximidad al coste target → misma gama → resto.
              {costeTargetUsd != null && <> Coste target: <b>{fmt.usd(costeTargetUsd)}</b>.</>}
            </p>
          </div>
          <SugerenciasList
            loading={sugerencias.isFetching}
            data={sugerencias.data?.data ?? []}
            target={costeTargetUsd}
            subcategoriaNombre={np.subcategoria_nombre}
            emptyError={(sugerencias.data?.contexto as { error?: string } | undefined)?.error ?? null}
            onAsociar={(ppId) => addCandidato.mutate({ producto_proveedor_id: ppId }, {
              onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevo-producto', id, 'sugerencias-candidatos'] }),
            })}
            onPreview={(ppId) => setPreviewPP(ppId)}
            isAdding={addCandidato.isPending}
          />
          <div className="border-t border-slate-200 pt-3 text-xs text-slate-500">
            ¿No está? Créalo primero en{' '}
            <span onClick={() => navigate('/proveedores/productos')} className="cursor-pointer text-blue-600 underline" role="link">
              Productos de proveedor
            </span>.
          </div>
        </div>
      </Modal>

      {/* ─── Modal: editar candidato (valoración / pros / contras) ── */}
      <Modal
        open={editCandId != null}
        onClose={() => { setEditCandId(null); setCandDraft(null); }}
        title="Editar candidato"
        footer={
          <>
            <Button variant="danger" onClick={() => { setEditCandId(null); setCandDraft(null); }} disabled={valorar.isPending}>
              Cancelar
            </Button>
            <Button
              variant="save"
              onClick={async () => {
                if (!editCandId || !candDraft) return;
                await valorar.mutateAsync({
                  candId: editCandId,
                  body: {
                    valoracion: candDraft.valoracion === '' ? null : Number(candDraft.valoracion),
                    pros: candDraft.pros.trim() || null,
                    contras: candDraft.contras.trim() || null,
                  },
                });
                setEditCandId(null);
                setCandDraft(null);
              }}
              disabled={valorar.isPending}
            >
              {valorar.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </>
        }
      >
        {candDraft && (
          <div className="grid gap-3">
            <FormRow label="Valoración (1-5)">
              <Input
                type="number"
                min={1}
                max={5}
                value={candDraft.valoracion}
                onChange={(e) => setCandDraft((d) => (d ? { ...d, valoracion: e.target.value } : d))}
              />
            </FormRow>
            <FormRow label="Pros">
              <Textarea
                rows={3}
                value={candDraft.pros}
                onChange={(e) => setCandDraft((d) => (d ? { ...d, pros: e.target.value } : d))}
              />
            </FormRow>
            <FormRow label="Contras">
              <Textarea
                rows={3}
                value={candDraft.contras}
                onChange={(e) => setCandDraft((d) => (d ? { ...d, contras: e.target.value } : d))}
              />
            </FormRow>
          </div>
        )}
      </Modal>

      {/* ─── Modal: convertir a producto ─────────────────────────── */}
      <Modal
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        title="Convertir a producto"
        size="md"
        footer={
          <>
            <Button variant="danger" onClick={() => setConvertOpen(false)}>Cancelar</Button>
            <Button
              variant="create"
              onClick={() => convertir.mutate({
                sku: convertForm.sku,
                ean: convertForm.ean || undefined,
                coste: convertForm.coste === '' ? null : Number(convertForm.coste),
                pvpr: convertForm.pvpr === '' ? null : Number(convertForm.pvpr),
              })}
              disabled={!convertForm.sku || convertir.isPending}
            >
              Crear producto
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-slate-600">
          Se creará un producto real (estado EN_FABRICACION) con marca {np.marca_nombre}, ref. proveedor del ganador y enlace a este proyecto.
        </p>
        <div className="grid gap-3">
          <FormRow label="SKU" required><Input value={convertForm.sku} onChange={(e) => setConvertForm((f) => ({ ...f, sku: e.target.value }))} /></FormRow>
          <FormRow label="EAN"><Input value={convertForm.ean} onChange={(e) => setConvertForm((f) => ({ ...f, ean: e.target.value }))} /></FormRow>
          <FormRow label="Coste inicial (EUR)"><Input type="number" step="any" value={convertForm.coste} onChange={(e) => setConvertForm((f) => ({ ...f, coste: e.target.value }))} /></FormRow>
          <FormRow label="PVPr inicial (EUR)"><Input type="number" step="any" value={convertForm.pvpr} onChange={(e) => setConvertForm((f) => ({ ...f, pvpr: e.target.value }))} /></FormRow>
        </div>
        {!ganador && <p className="mt-3 text-sm text-rose-600">Primero marca un candidato como ganador.</p>}
      </Modal>

      <ProductoProveedorPreviewModal
        ppId={previewPP}
        onClose={() => setPreviewPP(null)}
        onAbrirFicha={(ppId) => { setPreviewPP(null); navigate(`/proveedores/productos/${ppId}`); }}
      />
    </div>
  );
}

// ─── Subcomponente: lista de sugerencias por bucket ───────────────────

function SugerenciasList({
  loading,
  data,
  target,
  onAsociar,
  onPreview,
  isAdding,
  subcategoriaNombre,
  emptyError,
}: {
  loading: boolean;
  data: SugerenciaCandidato[];
  target: number | null;
  onAsociar: (id: string) => void;
  onPreview: (id: string) => void;
  isAdding: boolean;
  subcategoriaNombre?: string | null;
  emptyError?: string | null;
}) {
  if (loading) {
    return <p className="text-sm text-slate-500">Buscando…</p>;
  }
  if (data.length === 0) {
    if (emptyError === 'sin_subtipo') {
      return (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Este proyecto no tiene <strong>subtipo</strong> asignado. Edita la ficha y define la
          subcategoría para poder buscar candidatos del mismo subtipo.
        </div>
      );
    }
    return (
      <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        No hay productos del subtipo <strong>{subcategoriaNombre ?? '—'}</strong> en el catálogo
        del proveedor. Crea el producto primero en{' '}
        <a href="/proveedores/productos/nuevo" className="cursor-pointer text-sky-600 hover:underline">
          Productos de proveedor → Añadir producto
        </a>{' '}
        y vuelve a esta ficha.
      </div>
    );
  }
  // Agrupar por bucket
  const groups: { bucket: number; items: SugerenciaCandidato[] }[] = [];
  for (const item of data) {
    const last = groups[groups.length - 1];
    if (last && last.bucket === item.bucket) last.items.push(item);
    else groups.push({ bucket: item.bucket, items: [item] });
  }
  return (
    <div className="max-h-[60vh] space-y-4 overflow-y-auto">
      {groups.map((g) => (
        <div key={g.bucket}>
          <div className="sticky top-0 bg-white pb-2">
            <Badge color={BUCKET_BADGE_COLOR[g.bucket] ?? '#94a3b8'}>{BUCKET_LABEL[g.bucket] ?? `Bucket ${g.bucket}`}</Badge>
            <span className="ml-2 text-xs text-slate-500">{g.items.length} resultado{g.items.length === 1 ? '' : 's'}</span>
          </div>
          <ul className="divide-y divide-slate-100 rounded border border-slate-200">
            {g.items.map((it) => {
              const coste = it.coste_usd != null ? Number(it.coste_usd) : null;
              const diffPct = target != null && coste != null && target > 0
                ? ((coste - target) / target) * 100
                : null;
              const okPrice = diffPct != null && diffPct <= 0;
              return (
                <li key={it.id} className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-50">
                  <div className="min-w-0">
                    <p
                      className="cursor-pointer truncate text-sm font-semibold text-slate-900 hover:underline"
                      onClick={() => onPreview(it.id)}
                      title="Ver ficha reducida"
                    >
                      <span className="font-mono text-xs text-slate-500">{it.ref_proveedor}</span> · {it.nombre}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {it.proveedor_nombre ?? '—'} · {it.gama_nombre ?? '—'} / {it.categoria_nombre ?? '—'} / {it.subcategoria_nombre ?? '—'}
                    </p>
                    {it.moq != null || it.lead_time_dias != null ? (
                      <p className="text-xs text-slate-500">
                        {it.moq != null && <>MOQ {fmt.number(it.moq)}</>}
                        {it.moq != null && it.lead_time_dias != null && ' · '}
                        {it.lead_time_dias != null && <>Lead {it.lead_time_dias}d</>}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="font-mono text-sm font-semibold text-slate-900">{fmt.usd(coste)}</span>
                    {diffPct != null && (
                      <Badge color={okPrice ? '#10b981' : '#dc2626'}>
                        {okPrice ? '−' : '+'}{Math.abs(diffPct).toFixed(1)}% vs target
                      </Badge>
                    )}
                    <Button size="sm" disabled={isAdding} onClick={() => onAsociar(it.id)}>
                      Asociar
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ─── Modal de ficha reducida del producto de proveedor ────────────────

type PPArchivo = { id: string; tipo: string; storage_path: string; nombre_original: string | null; mime_type: string | null; url?: string | null };
type PPPrecio = {
  id: string;
  fecha: string;
  tramo1_unidades: number | null;
  tramo1_precio_usd: number | string | null;
  tramo2_unidades: number | null;
  tramo2_precio_usd: number | string | null;
  tramo3_unidades: number | null;
  tramo3_precio_usd: number | string | null;
  opciones: Array<{ nombre?: string; descripcion?: string; precio_usd?: number | string | null }> | null;
  notas: string | null;
};
type PPFull = {
  id: string;
  ref_proveedor: string;
  nombre: string;
  descripcion: string | null;
  main_specs: string | null;
  coste_usd: number | string | null;
  moq: number | null;
  lead_time_dias: number | null;
  envio_tipo: string;
  packaging_included: boolean | null;
  foc_pct: number | string | null;
  proveedor_nombre: string | null;
  gama_nombre: string | null;
  categoria_nombre: string | null;
  subcategoria_nombre: string | null;
  detalle_nombre: string | null;
  precios: PPPrecio[];
  archivos: PPArchivo[];
};

function ProductoProveedorPreviewModal({
  ppId,
  onClose,
  onAbrirFicha,
}: {
  ppId: string | null;
  onClose: () => void;
  onAbrirFicha?: (ppId: string) => void;
}) {
  const data = useQuery({
    queryKey: ['producto-proveedor', ppId, 'preview-full'],
    queryFn: () => api.get<PPFull>(`/productos-proveedor/${ppId}/full`),
    enabled: !!ppId,
  });
  const archivosConUrl = useQuery({
    queryKey: ['producto-proveedor', ppId, 'archivos-firmados'],
    queryFn: () => api.get<{ data: PPArchivo[] }>(`/productos-proveedor/${ppId}/archivos`),
    enabled: !!ppId,
  });

  if (!ppId) return null;
  const pp = data.data;
  const imagenPrincipal = (archivosConUrl.data?.data ?? pp?.archivos ?? []).find((a) => a.tipo === 'imagen_principal');
  const ultimoPrecio = pp?.precios[0];
  const opciones = ultimoPrecio?.opciones ?? [];

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={pp ? `${pp.ref_proveedor} · ${pp.nombre}` : 'Cargando…'}
      size="xl"
      footer={
        onAbrirFicha && pp ? (
          <>
            <Button variant="secondary" onClick={onClose}>Cerrar</Button>
            <Button variant="create" onClick={() => onAbrirFicha(pp.id)}>Abrir ficha completa →</Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
        )
      }
    >
      {data.isLoading || !pp ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-12">
          <div className="md:col-span-4">
            {imagenPrincipal?.url ? (
              <img
                src={imagenPrincipal.url}
                alt={pp.nombre}
                className="aspect-square w-full rounded-lg border border-slate-200 object-contain"
              />
            ) : (
              <div className="flex aspect-square w-full items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-4xl text-slate-400">
                📦
              </div>
            )}
          </div>

          <div className="space-y-3 md:col-span-8">
            <div className="text-xs text-slate-500">
              {pp.proveedor_nombre ?? '—'} · {pp.gama_nombre ?? '—'} / {pp.categoria_nombre ?? '—'} / {pp.subcategoria_nombre ?? '—'}
              {pp.detalle_nombre ? ` / ${pp.detalle_nombre}` : ''}
            </div>

            <div className="grid gap-2 text-sm sm:grid-cols-3">
              <div>
                <p className="text-xs uppercase text-slate-500">Precio</p>
                <p className="font-mono font-semibold">{fmt.usd(pp.coste_usd)}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">MOQ</p>
                <p className="font-semibold">{fmt.number(pp.moq)}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Lead time</p>
                <p className="font-semibold">{pp.lead_time_dias ?? '—'} d</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">F.O.C.</p>
                <p className="font-semibold">{pp.foc_pct != null ? fmt.pct(pp.foc_pct) : '—'}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Envío</p>
                <p className="font-semibold">{pp.envio_tipo ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Packaging</p>
                <p className="font-semibold">{pp.packaging_included ? 'Incluido' : 'No incluido'}</p>
              </div>
            </div>

            {pp.main_specs && (
              <div>
                <p className="text-xs uppercase text-slate-500">Main specs</p>
                <p className="whitespace-pre-line text-sm text-slate-700">{pp.main_specs}</p>
              </div>
            )}

            {pp.descripcion && (
              <div>
                <p className="text-xs uppercase text-slate-500">Specs / descripción</p>
                <p className="whitespace-pre-line text-sm text-slate-700">{pp.descripcion}</p>
              </div>
            )}

            {opciones.length > 0 && (
              <div>
                <p className="text-xs uppercase text-slate-500">Opciones</p>
                <ul className="text-sm text-slate-700">
                  {opciones.map((o, i) => (
                    <li key={i} className="flex items-baseline justify-between gap-2 border-b border-slate-100 py-1 last:border-0">
                      <span>
                        <strong>{o.nombre ?? `Opción ${i + 1}`}</strong>
                        {o.descripcion && <span className="ml-2 text-xs text-slate-500">{o.descripcion}</span>}
                      </span>
                      <span className="font-mono text-sm">{o.precio_usd != null ? fmt.usd(o.precio_usd) : ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pp.precios.length > 0 && (
              <div>
                <p className="text-xs uppercase text-slate-500">Histórico de precios ({pp.precios.length})</p>
                <table className="mt-1 w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-2 py-1 text-left">Fecha</th>
                      <th className="px-2 py-1 text-right">T1 uds</th>
                      <th className="px-2 py-1 text-right">T1 USD</th>
                      <th className="px-2 py-1 text-right">T2 uds</th>
                      <th className="px-2 py-1 text-right">T2 USD</th>
                      <th className="px-2 py-1 text-right">T3 uds</th>
                      <th className="px-2 py-1 text-right">T3 USD</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pp.precios.slice(0, 6).map((pr) => (
                      <tr key={pr.id}>
                        <td className="px-2 py-1">{fmt.date(pr.fecha)}</td>
                        <td className="px-2 py-1 text-right">{fmt.number(pr.tramo1_unidades)}</td>
                        <td className="px-2 py-1 text-right font-mono">{fmt.usd(pr.tramo1_precio_usd)}</td>
                        <td className="px-2 py-1 text-right">{fmt.number(pr.tramo2_unidades)}</td>
                        <td className="px-2 py-1 text-right font-mono">{fmt.usd(pr.tramo2_precio_usd)}</td>
                        <td className="px-2 py-1 text-right">{fmt.number(pr.tramo3_unidades)}</td>
                        <td className="px-2 py-1 text-right font-mono">{fmt.usd(pr.tramo3_precio_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Columnas para la vista de lectura ────────────────────────────────

function DatosCol({ np, className }: { np: NuevoProductoFull; className?: string }) {
  const rows: Array<[string, React.ReactNode]> = [
    ['Importancia', <Badge key="i" color={IMPORTANCIA_COLOR[np.importancia]}>{IMPORTANCIA_LABEL[np.importancia]}</Badge>],
    ['Cliente', np.cliente_nombre ?? '—'],
    ['Marca', np.marca_nombre ?? '—'],
    ['Gama', np.gama_nombre ?? '—'],
    ['Categoría', np.categoria_nombre ?? '—'],
    ['Subcategoría', np.subcategoria_nombre ?? '—'],
    ['Detalle', np.detalle_nombre ?? '—'],
    ['Segmento', np.segmento ?? '—'],
    ['Modelo interno', np.modelo_interno ?? '—'],
    ['Compatibilidad', np.compatibilidad ?? '—'],
    ['Origen', np.origen ?? '—'],
    ['Fecha creación', fmt.date(np.created_at)],
    ['Última edición', fmt.datetime(np.updated_at)],
    ['Notas', np.notas ?? '—'],
  ];
  return (
    <Card title="Datos del proyecto" className={className}>
      <dl className="space-y-1.5 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-3">
            <dt className="shrink-0 font-medium uppercase tracking-wide text-[10px] text-slate-500">{k}:</dt>
            <dd className="break-words text-right text-slate-800 max-w-[70%] whitespace-pre-line">{v}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function shortenUrl(s: string, max = 48): string {
  if (s.length <= max) return s;
  try {
    const u = new URL(s);
    const path = u.pathname.length > 12 ? `…${u.pathname.slice(-10)}` : u.pathname;
    return `${u.hostname}${path}`;
  } catch {
    return s.slice(0, max - 1) + '…';
  }
}

function ExamplesCol({
  examples,
  onAdd,
  onRemove,
  value,
  onChangeValue,
  className,
}: {
  examples: string[];
  onAdd: (s: string) => void;
  onRemove: (i: number) => void;
  value: string;
  onChangeValue: (s: string) => void;
  className?: string;
}) {
  const submit = () => {
    const v = value.trim();
    if (!v) return;
    onAdd(v);
    onChangeValue('');
  };
  return (
    <Card title={`Examples (${examples.length})`} description="URLs y referencias. Enter para añadir." className={className}>
      <div className="flex gap-2">
        <Input
          value={value}
          placeholder="https://… o texto"
          onChange={(e) => onChangeValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        />
        <Button variant="create" size="sm" disabled={!value.trim()} onClick={submit}>+</Button>
      </div>
      {examples.length === 0 ? (
        <p className="mt-3 text-xs italic text-slate-400">Sin ejemplos.</p>
      ) : (
        <ul className="mt-3 space-y-1">
          {examples.map((ex, idx) => {
            const isLink = isUrl(ex);
            return (
              <li key={`${idx}-${ex}`} className="flex items-center justify-between gap-2 rounded px-1 py-0.5 hover:bg-slate-50">
                {isLink ? (
                  <a
                    href={ex}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate text-xs text-blue-600 hover:underline"
                    title={ex}
                  >
                    {shortenUrl(ex)}
                  </a>
                ) : (
                  <span className="truncate text-xs text-slate-700" title={ex}>{ex}</span>
                )}
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="cursor-pointer text-[10px] text-rose-600 hover:underline"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function ImagenesCol({
  archivos,
  onUpload,
  uploading,
  onDelete,
  className,
}: {
  archivos: Archivo[];
  onUpload: (f: File) => void;
  uploading: boolean;
  onDelete: (id: string) => void;
  className?: string;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const imagenes = archivos.filter((a) => a.tipo === 'imagen');

  // Paste handler (Ctrl+V con foco fuera de inputs)
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const active = document.activeElement;
      if (active && /^(INPUT|TEXTAREA)$/.test(active.tagName)) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            onUpload(file);
            break;
          }
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [onUpload]);

  return (
    <Card
      title={`Imágenes (${imagenes.length})`}
      description="Pega (Ctrl+V), arrastra o pulsa para subir."
      actions={
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              if (fileRef.current) fileRef.current.value = '';
            }}
          />
          <Button variant="create" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? '…' : '+'}
          </Button>
        </>
      }
      className={className}
    >
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files?.[0];
          if (file && file.type.startsWith('image/')) onUpload(file);
        }}
        className="grid grid-cols-3 gap-2"
      >
        {imagenes.map((a) => (
          <div key={a.id} className="group relative aspect-square overflow-hidden rounded border border-slate-200">
            {a.url ? (
              <img
                src={a.url}
                alt={a.nombre_original}
                className="h-full w-full cursor-zoom-in object-cover"
                onClick={() => a.url && setLightbox(a.url)}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-slate-100 text-xs text-slate-400">—</div>
            )}
            <button
              type="button"
              className="absolute right-0 top-0 hidden rounded-bl bg-rose-600 px-1.5 py-0.5 text-[10px] text-white group-hover:block"
              onClick={async () => { if (await confirmar({ title: '¿Eliminar imagen?', confirmLabel: 'Eliminar', danger: true })) onDelete(a.id); }}
            >
              ×
            </button>
          </div>
        ))}
        {imagenes.length === 0 && (
          <p className="col-span-3 text-xs italic text-slate-400">Sin imágenes.</p>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-black/80 p-8"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="Ampliada" className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </Card>
  );
}

// ─── Tabla comparativa: productos en columnas ─────────────────────────

function CandidatoImagenMini({ ppId }: { ppId: string }) {
  const archivos = useQuery({
    queryKey: ['producto-proveedor', ppId, 'archivos-firmados'],
    queryFn: () => api.get<{ data: { id: string; tipo: string; url: string | null }[] }>(`/productos-proveedor/${ppId}/archivos`),
    enabled: !!ppId,
  });
  const principal = archivos.data?.data?.find((a) => a.tipo === 'imagen_principal');
  if (principal?.url) {
    return <img src={principal.url} alt="" className="h-20 w-20 rounded border border-slate-200 object-contain" />;
  }
  return (
    <div className="flex h-20 w-20 items-center justify-center rounded border border-dashed border-slate-200 bg-slate-50 text-xl text-slate-300">
      📦
    </div>
  );
}

function ComparativaTable({
  candidatos,
  target,
  onClickPP,
}: {
  candidatos: Candidato[];
  target: number | null;
  onClickPP: (ppId: string) => void;
}) {
  const filas: Array<{ label: string; cell: (c: Candidato) => React.ReactNode }> = [
    { label: 'REF', cell: (c) => <span className="font-mono text-xs">{c.ref_proveedor}</span> },
    { label: 'IMAGEN', cell: (c) => <CandidatoImagenMini ppId={c.producto_proveedor_id} /> },
    {
      label: 'PRECIO',
      cell: (c) => {
        const coste = c.coste_usd != null ? Number(c.coste_usd) : null;
        const diff = target != null && coste != null && target > 0 ? ((coste - target) / target) * 100 : null;
        return (
          <div className="space-y-0.5">
            <div className="font-mono font-semibold">{fmt.usd(coste)}</div>
            {diff != null && (
              <div className={`text-[10px] font-semibold ${diff <= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {diff <= 0 ? '−' : '+'}{Math.abs(diff).toFixed(1)}% target
              </div>
            )}
          </div>
        );
      },
    },
    { label: 'PROVEEDOR', cell: (c) => <span className="text-xs">{c.proveedor_nombre ?? '—'}</span> },
    {
      label: 'NOMBRE',
      cell: (c) => (
        <button
          type="button"
          onClick={() => onClickPP(c.producto_proveedor_id)}
          className="cursor-pointer text-left text-xs text-slate-800 hover:underline"
          title={c.pp_nombre}
        >
          {c.pp_nombre}
        </button>
      ),
    },
    {
      label: 'MAIN SPECS',
      cell: (c) => (
        c.pp_main_specs
          ? <p className="whitespace-pre-line text-left text-xs text-slate-700">{c.pp_main_specs}</p>
          : <span className="text-xs text-slate-300">—</span>
      ),
    },
    { label: 'MOQ', cell: (c) => <span className="text-xs">{fmt.number(c.moq)}</span> },
    {
      label: 'F.O.C.',
      cell: (c) => {
        const foc = c.pp_foc_pct ?? c.proveedor_foc_pct ?? null;
        return <span className="text-xs">{foc != null ? fmt.pct(foc) : '—'}</span>;
      },
    },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="border-collapse text-sm">
        <thead>
          <tr>
            <th className="w-28 bg-slate-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500"></th>
            {candidatos.map((c, idx) => (
              <th
                key={c.id}
                className={`min-w-[10rem] border-l border-slate-200 px-3 py-2 text-center ${c.seleccionado ? 'bg-emerald-50' : 'bg-white'}`}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  #{idx + 1}
                  {c.seleccionado && <span className="ml-1"><Badge color="#10b981">★</Badge></span>}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filas.map((f) => (
            <tr key={f.label} className="align-top">
              <td className="bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                {f.label}
              </td>
              {candidatos.map((c) => (
                <td
                  key={c.id}
                  className={`border-l border-slate-200 px-3 py-2 text-center ${c.seleccionado ? 'bg-emerald-50/40' : ''}`}
                >
                  {f.cell(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
